/**
 * Craftpix Poker Game Kit (552281) asset map — SSOT for lobby / tables UI chrome.
 * Source: public/poker-ui/** (synced from craftpix-552281-poker-game-kit).
 *
 * Best practice:
 *   - BG / panels / felts / chips from craftpix
 *   - **Buttons = system font only** (no pre-written fold/play/shop sprites under labels)
 *   - Word-art paths below kept for archive reference only — UI must not render them as primary chrome
 */

export type CraftpixTheme = "green" | "red" | "violet" | "blue";

/**
 * Deployed SSOT is client/public/poker-ui (craftpix kit).
 * Keep /poker-assets as optional alias only if mirrored.
 */
const ROOT = "/poker-ui";

export const CRAFTPIX = {
  root: ROOT,
  lobby: {
    bgGreen: `${ROOT}/lobby/background_green.png`,
    bgRed: `${ROOT}/lobby/background_red.png`,
    bgViolet: `${ROOT}/lobby/background_violet.png`,
    panelBar: `${ROOT}/lobby/background-panel.png`,
    window: `${ROOT}/lobby/window.png`,
    resume: `${ROOT}/lobby/background-resume.png`,
    blinks: `${ROOT}/lobby/blinks.png`,
    tableGreen: `${ROOT}/lobby/table-green.png`,
    tableRed: `${ROOT}/lobby/table-red.png`,
    tableBlue: `${ROOT}/lobby/table-blue.png`,
    moneyPanel: `${ROOT}/lobby/money_score_panel.png`,
    chipPanel: `${ROOT}/lobby/chip_score_panel.png`,
    btnPlay: `${ROOT}/lobby/button_play.png`,
    btnWatch: `${ROOT}/lobby/button_whatch.png`,
    btnLobby: `${ROOT}/lobby/button_lobby.png`,
    btnShop: `${ROOT}/lobby/button_shop.png`,
    btnSetting: `${ROOT}/lobby/button_setting.png`,
    btnLeft: `${ROOT}/lobby/button_left.png`,
    btnRight: `${ROOT}/lobby/button_right.png`,
    avatar: `${ROOT}/lobby/defaul_avatar.png`,
    croupier: `${ROOT}/lobby/woman_ingame.png`,
  },
  menu: {
    bgGreen: `${ROOT}/menu/background_green.png`,
    bgRed: `${ROOT}/menu/background_red.png`,
    bgViolet: `${ROOT}/menu/background_violet.png`,
    elements: `${ROOT}/menu/background_elements.png`,
    playerPanel: `${ROOT}/menu/player_panel.png`,
    iconChip: `${ROOT}/menu/icon_chip.png`,
    iconCoin: `${ROOT}/menu/icon_coin.png`,
    btnPlay: `${ROOT}/menu/button_play.png`,
    btnLobby: `${ROOT}/menu/button_lobby.png`,
    btnShop: `${ROOT}/menu/button_shop.png`,
    btnSetting: `${ROOT}/menu/button_setting.png`,
    btnFriends: `${ROOT}/menu/button_friends.png`,
    btnChat: `${ROOT}/menu/button_chat.png`,
    avatarField: `${ROOT}/menu/default_avatar_field.png`,
  },
  game: {
    darkOverlay: `${ROOT}/interface_game/Dark-level-for-background.png`,
    panel: `${ROOT}/interface_game/background-panel.png`,
    moneyPanel: `${ROOT}/interface_game/money_score_panel.png`,
    chipPanel: `${ROOT}/interface_game/chip_score_panel.png`,
    avatar: `${ROOT}/interface_game/default_avatar.png`,
    tableGreen: `${ROOT}/interface_game/table-green.png`,
    tableRed: `${ROOT}/interface_game/table-red.png`,
    tableBlue: `${ROOT}/interface_game/table-blue.png`,
    /** @deprecated Word-art — do not show under live labels */
    fold: `${ROOT}/interface_game/button_fold.png`,
    check: `${ROOT}/interface_game/button_check.png`,
    call: `${ROOT}/interface_game/button_call.png`,
    raise: `${ROOT}/interface_game/button_raise.png`,
    timePanel: `${ROOT}/interface_game/timeleft_panel.png`,
    timeFill: `${ROOT}/interface_game/timeleft_fill.png`,
    playerAvatar: `${ROOT}/interface_game/avatar_player.png`,
  },
  chips: {
    root: "/chips",
    of: (n: number) => `/chips/${n}.png`,
  },
  /** Generated classy layers + BUDB branded felts */
  generated: {
    ambientLobby: "/media/brand/budb-table-hero.jpg",
    /**
     * Container plate art (wood grain) — replaces flat purple/zinc panels.
     * SSOT paths also on BRAND.panels; keep craftpix path for CraftpixContainer.
     */
    panelLuxury: "/media/poker/panel-wood.webp",
    panelWood: "/media/poker/panel-wood.webp",
    /** Suit-motif page background (was solid purple gradients) */
    panelFeltPattern: "/media/poker/panel-felt-pattern.webp",
    appIcon: "/branding/app-icon.png",
    tableThumbNl: "/media/felt-budb-green.jpg",
    /** HU / WSOP-style concept — blue felt SSOT */
    tableThumbHu: "/media/felt-budb-blue.jpg",
    lobbyTableHero: "/media/felt-budb-green.jpg",
    feltGreen: "/media/felt-budb-green.jpg",
    feltRed: "/media/felt-budb-red.jpg",
    feltBlue: "/media/felt-budb-blue.jpg",
    /**
     * Portrait phone felt: blue base + CSS navy/cyan rim (phonepoker3).
     * Visual SSOT reference: `/media/phone-poker-example.png`
     */
    feltVerticalPhone: "/media/felt-budb-blue.jpg",
    feltVerticalPhoneLegacy: "/media/felt-vertical-phone.jpg",
    cardBackProduction: "/media/card-back-production.jpg",
    /** Gold monogram SSOT — remade from table logo source (images/15 → 28) */
    logoMark: "/media/budb-pkr-logo-mark.jpg",
    logoMarkBranding: "/branding/budb-pkr-logo-mark.jpg",
    logoMarkBlack: "/media/logo/budb-pkr-mark-black.jpg",
    logoSourceTable: "/media/logo/source-table-monogram.jpg",
    /** Dealer button / puck (session images/16) */
    dealerButton: "/media/dealer-button.jpg",
    /** Card back — gold monogram logo (images/28 → card face) */
    cardBack: "/media/card-back-budb.jpg",
    /** phonepoker3 — phone layout color/placement reference (not a live felt texture) */
    phoneExample: "/media/phone-poker-example.png",
  },
} as const;

export function themeBackground(theme: CraftpixTheme): string {
  switch (theme) {
    case "red":
      return CRAFTPIX.lobby.bgRed;
    case "violet":
      return CRAFTPIX.lobby.bgViolet;
    case "blue":
      return CRAFTPIX.lobby.bgGreen; // blue uses green felt BG + blue table
    default:
      return CRAFTPIX.lobby.bgGreen;
  }
}

export function tablePreviewSrc(theme: CraftpixTheme): string {
  // Prefer generated BUDB-logo felts; craftpix paths remain as fallbacks
  switch (theme) {
    case "red":
      return CRAFTPIX.generated.feltRed;
    case "violet":
    case "blue":
      return CRAFTPIX.generated.feltBlue;
    default:
      return CRAFTPIX.generated.feltGreen;
  }
}

/** Map table kind → craftpix felt theme */
export function tableTheme(opts: {
  isAI?: boolean;
  isHu?: boolean;
  isTournament?: boolean;
  bigBlind?: number;
}): CraftpixTheme {
  if (opts.isAI) return "violet";
  if (opts.isHu) return "blue";
  if (opts.isTournament || (opts.bigBlind ?? 0) >= 50) return "red";
  return "green";
}
